import { useReducer, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import deviceAuthSettingsReducer from '../functions/deviceAuthSettingsReducer';

/**
 * デバイス認証によるアクセス制御設定の状態を管理するカスタムフック
 * @returns {Object} デバイス認証設定の状態と操作関数
 *
 */
const useDeviceAuthSettings = () => {
  /**
   * デバイス認証設定の初期状態を取得(APIから取得したデータを使用)
   */
  const deviceAuthSettingsFromState = useSelector(
    state => state.deviceAuth.deviceAuthSettings,
  );
  const groups = useSelector(state => state.group.groups);

  const initialDeviceAuthSettings = useMemo(() => {
    // subscriptions配列をoperatorIds配列に変換
    const operatorIds = deviceAuthSettingsFromState.subscriptions?.map(
      subscription => subscription.operatorId,
    );

    const newDeviceAuthSettings = {
      ...deviceAuthSettingsFromState,
      subscriptions: {
        operatorIds,
        groupId: groups.find(group => group.isDefault)?.id || groups[0]?.id,
      },
    };

    return newDeviceAuthSettings;
  }, [deviceAuthSettingsFromState, groups]);

  /**
   * アクセス制御設定の状態を管理
   */
  const [deviceAuthSettings, dispatch] = useReducer(
    deviceAuthSettingsReducer,
    initialDeviceAuthSettings,
  );

  /**
   * デバイス認証設定を更新
   */
  useEffect(() => {
    dispatch({
      type: 'setDeviceAuthSettings',
      settings: {
        isEnabled: deviceAuthSettingsFromState.isEnabled,
        ipAddresses: deviceAuthSettingsFromState.ipAddresses,
        subscriptions: {
          operatorIds: deviceAuthSettingsFromState?.subscriptions?.map(
            operator => operator.operatorId,
          ),
          groupId: deviceAuthSettings?.subscriptions?.groupId,
        },
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceAuthSettingsFromState]);

  useEffect(() => {
    dispatch({
      type: 'selectedGroup',
      groupId: groups.find(group => group.isDefault)?.id || groups[0]?.id,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups]);

  /**
   * アクセス制御を有効化/無効化する
   * @param {boolean} isEnabled - アクセス制御を有効化するかどうか
   */
  const handleRadioChange = isEnabled => {
    dispatch({
      type: 'toggleDeviceAuth',
      isEnabled,
    });
  };

  /**
   * メール送信先ユーザーを更新
   * @param {array} subscriptions - メール送信先ユーザーのIDリスト
   */
  const handleSubscriptionsChange = operatorIds => {
    dispatch({
      type: 'updateSubscriptions',
      operatorIds,
    });
  };

  /**
   * IPアドレスを追加
   * @param {string} ipAddress
   */
  const addedIpAddress = ipAddress => {
    dispatch({
      type: 'addedIpAddress',
      ipAddress,
    });
  };

  /**
   * IPアドレスを削除
   * @param {number} index - 削除するIPアドレスのインデックス
   */
  const removedIpAddress = index => {
    dispatch({
      type: 'removedIpAddress',
      index,
    });
  };

  /**
   * グループを選択
   * @param {string} groupId - 選択したグループID
   */
  const selectedGroup = groupId => {
    dispatch({
      type: 'selectedGroup',
      groupId,
    });
  };

  return {
    deviceAuthSettings,
    handleRadioChange,
    handleSubscriptionsChange,
    addedIpAddress,
    removedIpAddress,
    selectedGroup,
  };
};

export default useDeviceAuthSettings;
